import { ResourceItemSimpleList } from "@wayflyer/flyui";
import { IconCheckCircle24Line, IconEmptyPage24Line } from "@wayflyer/flyui-icons/24/line";
import type { FundingApplicationSchemas } from "api-types";
import React from "react";
import { type IntlShape } from "react-intl";

import { getReconnectConnectionDetailsText } from "../reconnect-connections";

import {
  getAdditionalRevenueResponseChildren,
  getAdditionalRevenueSourceDetailsText,
} from "./item-utils/additional-revenue-source";
import { getBankStatementsDetailsText } from "./item-utils/bank-statements";
import { getReconnectConnectionResponseChildren } from "./item-utils/reconnect-connections";
import { getAirItemLabel, listTruncation, type TaskResourceItemPropsWithId } from "./item-utils/shared";
import { MESSAGES } from "./message-definitions";

export const getAirResourceItems = (
  intl: IntlShape,
  airs: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema[],
  onAirCTA: (airId: string) => void
): TaskResourceItemPropsWithId[] => {
  const getAirDetailsText = (
    air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema
  ): React.ReactNode => {
    switch (air.type) {
      case "bank_statements_for_account":
        return getBankStatementsDetailsText(
          intl,
          air.data as FundingApplicationSchemas.BankStatementsForAccountAIRSchema
        );
      case "additional_revenue_source": {
        return getAdditionalRevenueSourceDetailsText(
          intl,
          air.data as FundingApplicationSchemas.AdditionalRevenueSourceAIRSchema
        );
      }
      case "reconnect_connection": {
        return getReconnectConnectionDetailsText(air.data as FundingApplicationSchemas.ReconnectConnectionAIRSchema);
      }
    }
  };

  // Show meta data from completed AIR response
  const getAirChildren = (
    air: FundingApplicationSchemas.AdditionalInformationRequestWithMetaSchema
  ): React.ReactNode => {
    if (!air.response && air.status !== "superseded") {
      return null;
    }
    if (air.status === "superseded") {
      const item = {
        icon: <IconCheckCircle24Line />,
        text: intl.formatMessage(MESSAGES.airStatusSupercededResponseText),
      };
      return <ResourceItemSimpleList items={[item]} />;
    }
    if (air.type === "bank_statements_for_account" && air.meta.length > 0) {
      const items = air.meta.map((meta) => ({
        icon: <IconEmptyPage24Line />,
        text: meta as string,
      }));
      return <ResourceItemSimpleList truncation={listTruncation(intl)} items={items} />;
    }
    if (air.type === "additional_revenue_source") {
      return getAdditionalRevenueResponseChildren(
        intl,
        air.meta as FundingApplicationSchemas.ConnectionMetadataSchema[],
        air.response as FundingApplicationSchemas.AdditionalRevenueSourceAIRResponse
      );
    }
    if (air.type === "reconnect_connection") {
      return getReconnectConnectionResponseChildren(intl, air);
    }
  };

  const airResourceItems = ((): TaskResourceItemPropsWithId[] => {
    return airs.map((air) => {
      if (air.status === "in_progress") {
        return {
          id: air.public_identifier,
          testId: air.public_identifier,
          label: getAirItemLabel(intl, air),
          detailsText: getAirDetailsText(air),
          action: {
            type: "button",
            onClick: () => onAirCTA(air.public_identifier),
            label: intl.formatMessage(MESSAGES.airCTAViewRequest),
          },
        };
      } else {
        return {
          id: air.public_identifier,
          label: getAirItemLabel(intl, air),
          detailsText: getAirDetailsText(air),
          status: "positive",
          statusText: intl.formatMessage(MESSAGES.airStatusCompleted),
          children: getAirChildren(air),
        };
      }
    });
  })();

  // Sort by status first: "in_progress" (undefined for resource item) comes before other statuses
  // If statuses are the same, sort by label alphabetically, then by public identifier (ascending)
  const sortedAirsItems = airResourceItems.sort((a, b) => {
    if (a.status === undefined && b.status !== undefined) {
      return -1;
    }
    if (a.status !== undefined && b.status === undefined) {
      return 1;
    }
    if (a.status === b.status) {
      return a.label.localeCompare(b.label);
    }
    return a.id.localeCompare(b.id);
  });

  return sortedAirsItems;
};
